import type { MapIssue } from "./types";

/** Reject malformed optional data before the geometric checker can dereference it. */
export function checkMapValues(input: unknown): MapIssue[] {
  const issues: MapIssue[] = [];
  const error = (path: string, message: string): void => { issues.push({ level: "error", code: "MAP_VALUE", path, message, fix: "Supply a finite value of the documented type; geometry is never repaired by guessing." }); };
  const object = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
  if (!object(input)) return [{ level: "error", code: "MAP_OBJECT", path: "", message: "a map must be a JSON object" }];
  const finite = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
  const coordinate = (v: unknown): v is number => finite(v) && Math.abs(v) <= 100000;
  const position = (v: unknown, p: string): void => {
    if (Array.isArray(v) ? v.length !== 2 || !v.every(coordinate) : !object(v) || !coordinate(v.s) || !coordinate(v.d)) error(p, "position must be finite [x, z] or {s, d}, within 100 km of the map origin");
  };
  const number = (v: unknown, p: string, min = -Infinity, max = Infinity): void => {
    if (v !== undefined && (!finite(v) || v < min || v > max)) error(p, `must be a finite number in [${min}, ${max}]`);
  };
  const bool = (v: unknown, p: string): void => { if (v !== undefined && typeof v !== "boolean") error(p, "must be boolean"); };
  const array = (v: unknown, p: string, cb: (v: Record<string, unknown>, p: string) => void): void => {
    if (v === undefined) return;
    if (!Array.isArray(v)) { error(p, "must be an array"); return; }
    v.forEach((entry, i) => { if (!object(entry)) error(`${p}[${i}]`, "must be an object"); else cb(entry, `${p}[${i}]`); });
  };
  const zone = (v: unknown, p: string): void => {
    if (!(finite(v) || (Array.isArray(v) && v.length === 2 && typeof v[0] === "string" && finite(v[1])))) error(p, "must be route metres or [anchor, offset]");
  };
  bool(input.research, "research"); number(input.timeoutS, "timeoutS", 1, 86400);
  array(input.roads, "roads", (r,p) => {
    if (Array.isArray(r.pts)) r.pts.forEach((q,i)=>position(q, `${p}.pts[${i}]`));
    if (r.center !== undefined) position(r.center, `${p}.center`);
    number(r.width, `${p}.width`, 1.2, 100); number(r.innerR, `${p}.innerR`, 0.01, 10000); number(r.outerR, `${p}.outerR`, 0.01, 10000);
    number(r.lanes, `${p}.lanes`, 1, 8); if (r.lanes !== undefined && !Number.isInteger(r.lanes)) error(`${p}.lanes`, "lane count must be an integer");
    for (const k of ["oneWay", "frame", "clockwise"]) bool(r[k], `${p}.${k}`);
  });
  for (const field of ["props", "decor", "actors"]) array(input[field], field, (v,p) => {
    if (field !== "actors" || v.at !== undefined) position(v.at, `${p}.at`);
    if (v.to !== undefined) position(v.to, `${p}.to`);
    if (v.path !== undefined) {
      if (!Array.isArray(v.path)) error(`${p}.path`, "must be an array");
      else v.path.forEach((q,i) => position(q, `${p}.path[${i}]`));
    }
    for (const k of ["halfW", "halfD", "height"]) number(v[k], `${p}.${k}`, 0.01, 50);
    for (const k of ["yaw", "facing"]) number(v[k], `${p}.${k}`);
    for (const k of ["speed", "cueDelayS", "walkAt", "holdIfRobotBehindM"]) number(v[k], `${p}.${k}`, 0, k === "speed" ? 30 : 100000);
    if (v.triggerAt !== null) number(v.triggerAt, `${p}.triggerAt`, 0, 100000);
    for (const k of ["across", "unmarked", "parkAtEnd", "kerbIdle", "canOvertake"]) bool(v[k], `${p}.${k}`);
    if (v.motion !== undefined && v.motion !== "forward" && v.motion !== "reverse") error(`${p}.motion`, "motion must be forward or reverse");
    if (v.role !== undefined && typeof v.role !== "string") error(`${p}.role`, "role must be text");
  });
  if (object(input.route)) {
    const r = input.route;
    array(r.waypoints, "route.waypoints", (v,p) => { position(v.at, `${p}.at`); number(v.halfWidth, `${p}.halfWidth`, 0.3, 50); number(v.fillet, `${p}.fillet`, 0, 1000); });
    array(r.directions, "route.directions", (v,p) => { if (v.arrive !== undefined) position(v.arrive, `${p}.arrive`); });
    array(r.turns, "route.turns", () => {});
    if (r.start !== undefined) { if (!object(r.start)) error("route.start", "must be an object"); else { position(r.start.at, "route.start.at"); number(r.start.heading, "route.start.heading"); } }
    if (r.destination !== undefined) position(r.destination, "route.destination");
  }
  for (const field of ["encounters", "sections"]) array(input[field], field, (v,p) => {
    zone(v.from, `${p}.from`); zone(v.to, `${p}.to`); bool(v.research, `${p}.research`);
    if (v.actors !== undefined && (!Array.isArray(v.actors) || !v.actors.every(x => typeof x === "string"))) error(`${p}.actors`, "must be an array of actor ids");
  });
  return issues;
}
